import {
  Actions,
  Button,
  ErrorAlert,
  Input,
  Modal,
  modalHandler,
  ModalOpenBtn,
  danger,
  success,
  SwitchInput,
  Table,
  UploadImageCard,
  useCanShow,
  useCustomForm,
  useDelete,
  useEffect,
  useLoading,
  useState,
  useTableColumns,
} from "@";
import { licensesFilter } from "./utils/helpers";
import { getLicenses, saveLicenses } from "./utils/api";
import { licensesColumns } from "./components/tablesColumns";
import { licensesDefaultValues, licensesSchema } from "./utils/validation";

const Licenses = () => {
  // State variables
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const filteredItems = licensesFilter(data, search);

  // Form handling
  const {
    reset,
    watch,
    errors,
    register,
    setValue,
    handleSubmit,
    isSubmitting,
    isSubmitSuccessful,
  } = useCustomForm(licensesDefaultValues, licensesSchema);

  // Loading and delete handlers
  const { isLoading, loading } = useLoading();
  const { deleteHandler } = useDelete(data, setData, "licenses");

  // Edit handler for populating the form with existing data
  const handlerEdit = (row) => {
    setIsEdit(true);
    reset({
      ...licensesDefaultValues,
      id: row.id,
      title: row.title,
      issuer: row.issuer,
      licence: null,
      issueDate: row.issueDate,
      imageFullUrl: row.licence,
      isDefault: row.isDefault,
    });
    modalHandler();
  };

  // Table columns definition
  const columns = useTableColumns(licensesColumns, (row) => (
    <>
      {useCanShow("editAssociationData") && (
        <Actions
          show
          notShow
          notDelete={row?.isDefault}
          row={row}
          edit={() => handlerEdit(row)}
          delete={deleteHandler}
        />
      )}
    </>
  ));

  // Fetch data function
  async function fetchData() {
    loading(true);
    try {
      const { data: res } = await getLicenses();
      setData(res.licenses);
    } catch (error) {
      setError(true);
    } finally {
      loading(false);
    }
  }

  // Clear form function
  const clearForm = () => {
    setIsEdit(false);
    reset(licensesDefaultValues);
  };

  // Submit form function
  const submit = async (form) => {
    try {
      await saveLicenses(form);
      success("تم الحفظ بنجاح");
      modalHandler();
      fetchData();
      clearForm();
    } catch (error) {
      danger("حدث خطأ أثناء الحفظ");
    }
  };

  // Fetch data on page load
  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      {/* Error alert */}
      <ErrorAlert reload={fetchData} show={error} />

      {/* Modal for adding/editing licenses */}
      <Modal fullHeight close={clearForm} title={isEdit ? "تعديل" : "اضافة"}>
        <form onSubmit={handleSubmit(submit)} className="relative p-2 md:p-5">
          {/* Title field */}
          <Input
            style="mb-5"
            label="اسم الترخيص"
            error={errors.title}
            input={register("title")}
          />

          {/* Issuer field */}
          <Input
            style="mb-5"
            error={errors.issuer}
            label="الجهة المصدرة للترخيص"
            input={register("issuer")}
          />

          {/* Issue date field */}
          <Input
            style="mb-7"
            type="datepicker"
            error={errors.issueDate}
            label="تاريخ انتهاء الترخيص"
            value={watch("issueDate")}
            input={register("issueDate")}
            change={(e) => setValue("issueDate", e)}
          />

          {/* Default license toggle */}
          <div className="mb-5">
            <SwitchInput
              label="الترخيص الافتراضي"
              checked={watch("isDefault")}
              change={(value) => setValue("isDefault", value)}
              isBordered={true}
            />
          </div>

          {/* Image field */}
          <UploadImageCard
            show
            title="صورة الترخيص"
            src={watch("imageFullUrl")}
            success={isSubmitSuccessful}
            star={!watch("imageFullUrl")}
            error={errors.licence?.message}
            upload={(file) => setValue("licence", file)}
          />

          {/* Submit button */}
          <Button isLoading={isSubmitting} type="submit" title="حفظ البيانات" />
        </form>
      </Modal>

      {/* Main table */}
      <main className="table-card">
        <div className="options-container lg:justify-between">
          {/* Main search field */}
          <input
            type="search"
            value={search}
            placeholder="بحث عن ترخيص"
            onChange={(e) => setSearch(e.target.value)}
            className="single-search-input"
          />

          {/* Add button */}
          {useCanShow("editAssociationData") && (
            <ModalOpenBtn title="اضافة ترخيص" />
          )}
        </div>

        {/* Table */}
        <Table
          localPg
          columns={columns}
          data={filteredItems}
          isLoading={isLoading}
        />
      </main>
    </>
  );
};

export default Licenses;
